/**
 * LayerGroupCard Component
 *
 * Collapsible group container for organizing layers.
 */

import React, { ReactNode } from 'react';
import './LayerGroupCard.scss';
import { Layer } from '../../../types/layer';

interface LayerGroupCardProps {
  title: string;
  layers: Layer[];
  isExpanded: boolean;
  onToggle: () => void;
  onToggleGroup?: (enable: boolean) => void;
  children: ReactNode;
  /** Override total count (useful for live layers where layers array may be empty) */
  totalCount?: number;
  /** Override enabled count (useful for live layers) */
  enabledCount?: number;
}

export const LayerGroupCard: React.FC<LayerGroupCardProps> = ({
  title,
  layers,
  isExpanded,
  onToggle,
  onToggleGroup,
  children,
  totalCount,
  enabledCount: enabledCountProp
}) => {
  // Use props if provided, otherwise calculate from layers array
  const layerCount = totalCount ?? layers.length;
  const enabledCount = enabledCountProp ?? layers.filter(layer => layer.enabled).length;
  const allEnabled = enabledCount === layerCount && layerCount > 0;
  const someEnabled = enabledCount > 0 && enabledCount < layerCount;

  const handleToggleGroup = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent expand/collapse
    if (onToggleGroup) {
      // If all enabled, disable all. Otherwise, enable all.
      onToggleGroup(!allEnabled);
    }
  };

  return (
    <div className={`layer-group-card ${isExpanded ? 'expanded' : ''}`}>
      <div className="layer-group-header">
        <button
          className="layer-group-expand-btn"
          onClick={onToggle}
          aria-expanded={isExpanded}
          aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${title}`}
        >
          <div className="layer-group-expand-icon">
            <span className="expand-arrow">▶</span>
          </div>

          <div className="layer-group-info">
            <div className="layer-group-title">{title}</div>
            <div className="layer-group-stats">
              {enabledCount > 0 && (
                <span className="layer-group-enabled">{enabledCount} active</span>
              )}
              <span className="layer-group-total">{layerCount} total</span>
            </div>
          </div>

          <div className="layer-group-badge">
            {layerCount}
          </div>
        </button>

        {onToggleGroup && (
          <button
            className={`layer-group-toggle-btn ${allEnabled ? 'all-enabled' : someEnabled ? 'some-enabled' : 'none-enabled'}`}
            onClick={handleToggleGroup}
            aria-label={allEnabled ? `Disable all ${title} layers` : `Enable all ${title} layers`}
            title={allEnabled ? 'Disable all' : 'Enable all'}
          >
            {allEnabled ? '☑' : someEnabled ? '◫' : '☐'}
          </button>
        )}
      </div>

      {isExpanded && (
        <div className="layer-group-content">
          {children}
        </div>
      )}
    </div>
  );
};
